import { useState, useEffect, useCallback } from "react";

interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  isOffline: boolean;
  hasUpdate: boolean;
  isStandalone: boolean;
  deferredPrompt: any;
}

interface PWAInstallOptions {
  title?: string;
  description?: string;
  icon?: string;
}

interface PushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export const usePWA = () => {
  const [pwaState, setPwaState] = useState<PWAState>({
    isInstallable: false,
    isInstalled: false,
    isOffline: false,
    hasUpdate: false,
    isStandalone: false,
    deferredPrompt: null,
  });

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pushSubscription, setPushSubscription] =
    useState<PushSubscription | null>(null);
  const [isPushSupported, setIsPushSupported] = useState(false);
  const [vapidPublicKey, setVapidPublicKey] = useState<string>("");
  const [swRegistered, setSwRegistered] = useState(false);
  const [isRegisteringPush, setIsRegisteringPush] = useState(false);
  const [lastPushRegistrationAttempt, setLastPushRegistrationAttempt] =
    useState(0);

  const checkStandalone = useCallback(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;
    setPwaState((prev) => {
      if (prev.isStandalone === isStandalone) return prev;
      return { ...prev, isStandalone };
    });
    return isStandalone;
  }, []);

  const checkInstallable = useCallback(() => {
    const isInstallable =
      !pwaState.isInstalled && pwaState.deferredPrompt !== null;
    setPwaState((prev) => ({ ...prev, isInstallable }));
    return isInstallable;
  }, [pwaState.isInstalled, pwaState.deferredPrompt]);

  const checkPushSupport = useCallback(() => {
    const supported = "serviceWorker" in navigator && "PushManager" in window;
    setIsPushSupported(supported);
    return supported;
  }, []);

  const fetchVapidPublicKey = useCallback(async () => {
    const envKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
    if (envKey) {
      setVapidPublicKey(envKey);
      return envKey;
    }

    try {
      const apiBaseUrl =
        import.meta.env.VITE_API_URL || "http://localhost:8000";
      const response = await fetch(`${apiBaseUrl}/api/push/vapid-public-key`);
      if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          if (data.success && data.public_key) {
            setVapidPublicKey(data.public_key);
            return data.public_key;
          }
        }
      }
    } catch (error) {
      console.warn(
        "Não foi possível buscar chave VAPID da API, usando fallback:",
        error
      );
    }

    return null;
  }, []);

  // Função para instalar o PWA
  const installPWA = useCallback(
    async (options?: PWAInstallOptions) => {
      if (!pwaState.deferredPrompt) {
        throw new Error("PWA não pode ser instalado no momento");
      }

      try {
        // Mostra o prompt de instalação
        const result = await pwaState.deferredPrompt.prompt();

        // Aguarda a resposta do usuário
        const choiceResult = await pwaState.deferredPrompt.userChoice;

        if (choiceResult.outcome === "accepted") {
          setPwaState((prev) => ({
            ...prev,
            isInstallable: false,
            isInstalled: true,
            deferredPrompt: null,
          }));

          // Limpa o prompt
          pwaState.deferredPrompt = null;

          return { success: true, message: "PWA instalado com sucesso!" };
        } else {
          return {
            success: false,
            message: "Instalação cancelada pelo usuário",
          };
        }
      } catch (error) {
        console.error("Erro ao instalar PWA:", error);
        throw error;
      }
    },
    [pwaState.deferredPrompt]
  );

  // Função para verificar atualizações
  const checkForUpdates = useCallback(async () => {
    if ("serviceWorker" in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
          return true;
        }
      } catch (error) {
        console.error("Erro ao verificar atualizações:", error);
      }
    }
    return false;
  }, []);

  // Função para aplicar atualizações
  const applyUpdate = useCallback(async () => {

    if ("serviceWorker" in navigator) {
      try {
        const registration = await navigator.serviceWorker.getRegistration();

        if (registration) {
          if (registration.waiting) {
            // Envia mensagem para o service worker aplicar a atualização
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
            return true;
          } else {
            // Verifica se há uma nova versão disponível
            await registration.update();
            return false;
          }
        } else {
          return false;
        }
      } catch (error) {
        console.error("Erro ao aplicar atualização:", error);
        return false;
      }
    } else {
      return false;
    }
  }, []);

  // Função para solicitar permissão de notificações
  const requestNotificationPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      throw new Error("Este navegador não suporta notificações");
    }

    if (Notification.permission === "granted") {
      return { permission: "granted" };
    }

    if (Notification.permission === "denied") {
      throw new Error("Permissão de notificação negada");
    }

    try {
      const permission = await Notification.requestPermission();
      return { permission };
    } catch (error) {
      console.error("Erro ao solicitar permissão:", error);
      throw error;
    }
  }, []);

  // Função para enviar notificação
  const sendNotification = useCallback(
    async (title: string, options?: NotificationOptions) => {
      if (!("Notification" in window)) {
        throw new Error("Este navegador não suporta notificações");
      }

      if (Notification.permission !== "granted") {
        throw new Error("Permissão de notificação não concedida");
      }

      try {
        // Preparar opções da notificação
        const notificationOptions = {
          icon: "/icons/android/android-launchericon-192-192.png",
          badge: "/icons/android/android-launchericon-96-96.png",
          ...options,
        };

        // Verificar se há service worker registrado
        if ("serviceWorker" in navigator) {
          try {
            const registration =
              await navigator.serviceWorker.getRegistration();
            if (registration && registration.active) {
              // Usar ServiceWorkerRegistration.showNotification (requerido no mobile)
              await registration.showNotification(title, notificationOptions);
              return;
            }
          } catch (swError) {
            console.warn(
              "Erro ao tentar usar service worker para notificação:",
              swError
            );
            // Continua para tentar o método alternativo
          }
        }

        // Fallback para new Notification() (desktop sem service worker ou como backup)
        try {
          const notification = new Notification(title, notificationOptions);
          return notification;
        } catch (constructorError) {
          console.error(
            "Erro ao usar Notification constructor:",
            constructorError
          );

          // Se ainda assim falhar, verificar se há service worker e tentar novamente
          if ("serviceWorker" in navigator) {
            const registration = await navigator.serviceWorker.ready;
            await registration.showNotification(title, notificationOptions);
            return;
          }

          throw constructorError;
        }
      } catch (error) {
        console.error("Erro ao enviar notificação:", error);
        throw error;
      }
    },
    []
  );

  // Função para registrar assinatura push
  const registerPushSubscription = useCallback(async () => {
    if (!isPushSupported) {
      throw new Error("Push notifications não são suportadas neste navegador");
    }

    try {
      // Solicitar permissão
      const permission = await requestNotificationPermission();
      if (permission.permission !== "granted") {
        throw new Error("Permissão de notificação negada");
      }

      // Obter chave VAPID pública
      let publicKey = vapidPublicKey;
      if (!publicKey) {
        publicKey = await fetchVapidPublicKey();
      }

      if (!publicKey) {
        throw new Error(
          "Chave VAPID pública não encontrada. Configure as chaves VAPID no backend."
        );
      }

      // Registrar service worker
      const registration = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // Obter assinatura push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as any,
      });

      // Converter para formato legível
      const pushSubscription: PushSubscription = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(
            String.fromCharCode.apply(
              null,
              new Uint8Array(subscription.getKey("p256dh") || [])
            )
          ),
          auth: btoa(
            String.fromCharCode.apply(
              null,
              new Uint8Array(subscription.getKey("auth") || [])
            )
          ),
        },
      };

      setPushSubscription(pushSubscription);

      // Enviar para o backend
      await sendPushSubscriptionToBackend(pushSubscription);

      return pushSubscription;
    } catch (error) {
      console.error("Erro ao registrar assinatura push:", error);
      throw error;
    }
  }, [
    isPushSupported,
    requestNotificationPermission,
    vapidPublicKey,
    fetchVapidPublicKey,
  ]);

  // Função para remover assinatura push
  const unregisterPushSubscription = useCallback(async () => {
    if (!isPushSupported || !pushSubscription) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      // Remover do backend
      await removePushSubscriptionFromBackend();

      setPushSubscription(null);
      return true;
    } catch (error) {
      console.error("Erro ao remover assinatura push:", error);
      return false;
    }
  }, [isPushSupported, pushSubscription]);

  // Função para verificar status da conexão
  const checkConnectionStatus = useCallback(() => {
    const isOnline = navigator.onLine;
    // Atualiza somente se houve mudança
    setIsOnline((prevOnline) => {
      if (prevOnline === isOnline) return prevOnline;
      return isOnline;
    });

    setPwaState((prev) => {
      const isOffline = !isOnline;
      if (prev.isOffline === isOffline) return prev;
      return { ...prev, isOffline };
    });

    return isOnline;
  }, []);

  // Função para enviar assinatura push para o backend
  const sendPushSubscriptionToBackend = async (
    subscription: PushSubscription
  ) => {
    try {
      // Gerar device_id estável baseado no endpoint (hash do endpoint)
      const endpointHash = btoa(subscription.endpoint)
        .replace(/[^a-zA-Z0-9]/g, "")
        .substring(0, 16);
      const deviceId = `device_${endpointHash}`;

      // Detectar nome do dispositivo
      const getDeviceName = () => {
        const userAgent = navigator.userAgent;
        if (/android/i.test(userAgent)) {
          return "Dispositivo Android";
        } else if (/iphone|ipad|ipod/i.test(userAgent)) {
          return "Dispositivo iOS";
        } else if (/windows/i.test(userAgent)) {
          return "Dispositivo Windows";
        } else if (/macintosh|mac os x/i.test(userAgent)) {
          return "Dispositivo macOS";
        } else if (/linux/i.test(userAgent)) {
          return "Dispositivo Linux";
        }
        return "Dispositivo Desconhecido";
      };

      // Preparar dados completos para o backend
      const subscriptionData = {
        ...subscription,
        device_id: deviceId,
        device_name: getDeviceName(),
        user_agent: navigator.userAgent,
      };

      // Usar a URL base da API configurada
      const apiBaseUrl =
        import.meta.env.VITE_API_URL || "http://localhost:8000";
      const response = await fetch(`${apiBaseUrl}/api/push/registrar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${
            localStorage.getItem("helpdesk_token") ||
            localStorage.getItem("token") ||
            ""
          }`,
        },
        body: JSON.stringify(subscriptionData),
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Resposta inválida do servidor" }));
        throw new Error(errorData.error || `Erro HTTP ${response.status}`);
      }

      const result = await response.json();
      return true;
    } catch (error) {
      console.error("Erro ao enviar assinatura push para o backend:", error);
      throw error;
    }
  };

  // Função para remover assinatura push do backend
  const removePushSubscriptionFromBackend = async () => {
    try {
      // Usar a URL base da API configurada
      const apiBaseUrl =
        import.meta.env.VITE_API_URL || "http://localhost:8000";
      const response = await fetch(`${apiBaseUrl}/api/push/remover`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${
            localStorage.getItem("helpdesk_token") ||
            localStorage.getItem("token") ||
            ""
          }`,
        },
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: "Resposta inválida do servidor" }));
        throw new Error(errorData.error || `Erro HTTP ${response.status}`);
      }

      const result = await response.json();
      return true;
    } catch (error) {
      console.error("Erro ao remover assinatura push do backend:", error);
      throw error;
    }
  };

  // Função para converter chave VAPID
  const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Função para registrar push subscription automaticamente quando permissão é concedida
  const registerPushSubscriptionAutomatically = useCallback(async () => {
    // Evitar execuções duplicadas
    if (isRegisteringPush) {
      return false;
    }

    // Evitar execuções muito frequentes (máximo 1 por minuto)
    const now = Date.now();
    if (now - lastPushRegistrationAttempt < 60000) {
      return false;
    }

    if (!isPushSupported) {
      return false;
    }

    setIsRegisteringPush(true);
    setLastPushRegistrationAttempt(now);

    try {
      // Verificar se já tem assinatura local
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        return false;
      }

      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        // Já tem assinatura local, verificar se já foi registrada recentemente
        const lastRegistered = localStorage.getItem("lastPushRegistration");
        const now = Date.now();
        if (lastRegistered && now - parseInt(lastRegistered) < 300000) {
          // 5 minutos
          return false;
        }


        // Converter para formato legível
        const pushSubscription: PushSubscription = {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(
              String.fromCharCode.apply(
                null,
                new Uint8Array(subscription.getKey("p256dh") || [])
              )
            ),
            auth: btoa(
              String.fromCharCode.apply(
                null,
                new Uint8Array(subscription.getKey("auth") || [])
              )
            ),
          },
        };

        setPushSubscription(pushSubscription);
        await sendPushSubscriptionToBackend(pushSubscription);
        localStorage.setItem("lastPushRegistration", now.toString());
        return true;
      }

      // Não tem assinatura local, tentar criar uma
      if (Notification.permission === "granted") {

        // Obter chave VAPID pública
        let publicKey = vapidPublicKey;
        if (!publicKey) {
          publicKey = await fetchVapidPublicKey();
        }

        if (!publicKey) {
          console.warn("Chave VAPID pública não encontrada");
          return false;
        }

        // Criar assinatura push
        const newSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey) as any,
        });

        // Converter para formato legível
        const pushSubscription: PushSubscription = {
          endpoint: newSubscription.endpoint,
          keys: {
            p256dh: btoa(
              String.fromCharCode.apply(
                null,
                new Uint8Array(newSubscription.getKey("p256dh") || [])
              )
            ),
            auth: btoa(
              String.fromCharCode.apply(
                null,
                new Uint8Array(newSubscription.getKey("auth") || [])
              )
            ),
          },
        };

        setPushSubscription(pushSubscription);
        await sendPushSubscriptionToBackend(pushSubscription);
        localStorage.setItem("lastPushRegistration", now.toString());
        return true;
      }

      return false;
    } catch (error) {
      console.error(
        "Erro ao registrar push subscription automaticamente:",
        error
      );
      return false;
    } finally {
      setIsRegisteringPush(false);
    }
  }, [
    isPushSupported,
    vapidPublicKey,
    fetchVapidPublicKey,
    sendPushSubscriptionToBackend,
    isRegisteringPush,
    lastPushRegistrationAttempt,
  ]);

  // Event listeners
  useEffect(() => {
    // Verifica estado inicial
    checkStandalone();
    checkConnectionStatus();
    checkPushSupport();

    // Busca chave VAPID pública
    fetchVapidPublicKey();

    // Registra o service worker e configura listeners de atualização
    const registerServiceWorker = async () => {
      if (swRegistered) {
        return;
      }

      if ("serviceWorker" in navigator) {
        try {
          // Verifica se já há um service worker registrado
          const existingRegistration =
            await navigator.serviceWorker.getRegistration();
          if (existingRegistration) {
            setSwRegistered(true);
            // Remove listeners antigos se existirem
            existingRegistration.removeEventListener("updatefound", () => {});
            return existingRegistration;
          }

          const registration = await navigator.serviceWorker.register("/sw.js");
          setSwRegistered(true);

          // Listener para mudanças no service worker
          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;

            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (
                  newWorker.state === "installed" &&
                  navigator.serviceWorker.controller
                ) {
                  setPwaState((prev) => ({ ...prev, hasUpdate: true }));
                }
              });
            }
          });

          // Listener para mudanças de estado - REMOVIDO O RELOAD AUTOMÁTICO
          navigator.serviceWorker.addEventListener("controllerchange", () => {
            setPwaState((prev) => ({ ...prev, hasUpdate: false }));
            // NÃO recarrega mais automaticamente para evitar loop infinito
          });

          return registration;
        } catch (error) {
          console.error("Erro ao registrar Service Worker:", error);
          return null;
        }
      }
      return null;
    };

    registerServiceWorker();

    // Listener para prompt de instalação
    const handleInstallPrompt = (event: CustomEvent) => {
      setPwaState((prev) => ({
        ...prev,
        deferredPrompt: event.detail.prompt,
        isInstallable: true,
      }));
    };

    // Listener para PWA instalado
    const handlePWAInstalled = () => {
      setPwaState((prev) => ({
        ...prev,
        isInstalled: true,
        isInstallable: false,
      }));
    };

    // Listener para atualizações disponíveis
    const handleUpdateAvailable = () => {
      setPwaState((prev) => ({ ...prev, hasUpdate: true }));
    };

    // Listener para mudanças de conectividade
    const handleOnline = () => {
      setIsOnline(true);
      setPwaState((prev) => ({ ...prev, isOffline: false }));
    };

    const handleOffline = () => {
      setIsOnline(false);
      setPwaState((prev) => ({ ...prev, isOffline: true }));
    };

    // Adiciona event listeners
    window.addEventListener(
      "pwa-install-available",
      handleInstallPrompt as EventListener
    );
    window.addEventListener("pwa-installed", handlePWAInstalled);
    window.addEventListener("pwa-update-available", handleUpdateAvailable);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Verifica periodicamente se está em modo standalone - reduced frequency
    // Note: reduzir frequência e evitar setState desnecessário evita loops de render
    const interval = setInterval(checkStandalone, 15000);

    // Verifica imediatamente se precisa registrar push subscription
    const checkAndRegisterAutomatically = async () => {
      if (Notification.permission === "granted") {
        await registerPushSubscriptionAutomatically();
      }
    };

    // Verifica imediatamente ao carregar
    checkAndRegisterAutomatically();

    return () => {
      // Remove event listeners
      window.removeEventListener(
        "pwa-install-available",
        handleInstallPrompt as EventListener
      );
      window.removeEventListener("pwa-installed", handlePWAInstalled);
      window.removeEventListener("pwa-update-available", handleUpdateAvailable);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);

      clearInterval(interval);
    };
  }, []); // Executa apenas no mount para evitar re-execuções causadas por mudanças internas

  // Verifica se pode ser instalado sempre que o estado muda
  useEffect(() => {
    const isInstallable =
      !pwaState.isInstalled && pwaState.deferredPrompt !== null;
    setPwaState((prev) => ({ ...prev, isInstallable }));
  }, [pwaState.isInstalled, pwaState.deferredPrompt]);

  return {
    ...pwaState,
    isOnline,
    isPushSupported,
    pushSubscription,
    vapidPublicKey,
    installPWA,
    checkForUpdates,
    applyUpdate,
    requestNotificationPermission,
    sendNotification,
    registerPushSubscription,
    unregisterPushSubscription,
    checkConnectionStatus,
  };
};
